/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import type {Actor} from './github/pullRequestTimelineTypes';
import type {ID, GitObject} from './github/types';

import ActorAvatar from './ActorAvatar';
import {CommentActionsMenu, EditCommentForm, useDeleteComment} from './CommentActions';
import PullRequestReviewCommentLineNumber from './PullRequestReviewCommentLineNumber';
import PullRequestReviewCommentPathLink from './PullRequestReviewCommentPathLink';
import TrustedRenderedMarkdown from './TrustedRenderedMarkdown';
import {gitHubPullRequestCommentForIDAtom} from './jotai';
import timeAgo from './timeAgo';
import {Box, Flash, Text} from '@primer/react';
import {useAtomValue} from 'jotai';
import {useCallback, useState} from 'react';

type Props = {
  comment: {
    id: ID;
    author?: Actor | null;
    originalCommit?: GitObject | null;
    path: string;
    body: string;
    bodyHTML: string;
    createdAt?: string;
    viewerCanUpdate: boolean;
    viewerCanDelete: boolean;
  };
  /** Whether this is a reply within a thread (not the first comment). */
  isReply?: boolean;
  /** Whether to show the file path (hidden for replies in the same thread). */
  showPath?: boolean;
};

export default function PullRequestReviewComment({
  comment,
  isReply = false,
  showPath = true,
}: Props): React.ReactElement {
  const reviewComment = useAtomValue(gitHubPullRequestCommentForIDAtom(comment.id));
  const commentID = comment.id;
  const commit = comment.originalCommit?.oid;
  const lineNumber = reviewComment?.originalLine;
  const startLineNumber = reviewComment?.originalStartLine ?? reviewComment?.startLine;
  const authorLogin = comment.author?.login;
  const [isEditing, setIsEditing] = useState(false);
  const {deleteComment, isDeleting, deleteError} = useDeleteComment(comment.id);

  const onEdit = useCallback(() => setIsEditing(true), []);
  const onCancelEdit = useCallback(() => setIsEditing(false), []);

  return (
    <Box
      className="PRT-review-comment"
      paddingY={2}
      paddingLeft={isReply ? 3 : 0}
      sx={{
        ...(isReply ? {
          borderLeftWidth: 2,
          borderLeftStyle: 'solid',
          borderLeftColor: 'border.default',
        } : {}),
        ...(isDeleting ? {opacity: 0.5, pointerEvents: 'none'} : {}),
      }}>
      {/* Author row */}
      <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={1}>
        <Box display="flex" alignItems="center" sx={{gap: 1}}>
          {authorLogin != null && (
            <>
              <ActorAvatar login={comment.author?.login} url={comment.author?.avatarUrl} size={18} />
              <Text fontSize={12} fontWeight="bold">
                {authorLogin}
              </Text>
            </>
          )}
          {comment.createdAt != null && (
            <Text fontSize={11} color="fg.muted">
              {timeAgo(comment.createdAt)}
            </Text>
          )}
        </Box>
        <CommentActionsMenu
          viewerCanUpdate={comment.viewerCanUpdate}
          viewerCanDelete={comment.viewerCanDelete}
          onEdit={onEdit}
          onDelete={deleteComment}
        />
      </Box>
      {deleteError != null && (
        <Flash variant="danger" sx={{mb: 1}}>
          {deleteError}
        </Flash>
      )}
      {/* File path row (only shown for first comment in thread or standalone comments) */}
      {showPath && (
        <Box
          display="flex"
          alignItems="center"
          color="accent.fg"
          fontSize={12}
          marginBottom={1}
          sx={{gap: 1}}>
          {commentID != null && commit != null && lineNumber != null && (
            <PullRequestReviewCommentLineNumber
              commentID={commentID}
              commit={commit}
              lineNumber={lineNumber}
              startLine={startLineNumber}
              path={comment.path}
            />
          )}
          {commentID != null && commit != null ? (
            <PullRequestReviewCommentPathLink
              commentID={commentID}
              commit={commit}
              path={comment.path}
            />
          ) : (
            <Box overflow="hidden" sx={{textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
              <Text fontSize={12} color="fg.muted">{comment.path}</Text>
            </Box>
          )}
        </Box>
      )}
      {/* Comment body */}
      {isEditing ? (
        <EditCommentForm
          commentId={comment.id}
          initialBody={comment.body}
          onCancel={onCancelEdit}
          onSaved={onCancelEdit}
        />
      ) : (
        <Box fontSize={1}>
          <TrustedRenderedMarkdown
            className="PRT-review-comment-text"
            trustedHTML={comment.bodyHTML}
          />
        </Box>
      )}
    </Box>
  );
}
